/**
 * eReg Settings — notification preferences (P-14: this page was a "coming
 * soon" stub with two nav entries pointing at it while the backend prefs
 * routes were live).
 */

import { useState, useEffect } from 'react';
import { Settings as SettingsIcon, Bell, Loader2, CheckCircle } from 'lucide-react';
import api from '../../services/api';
import { PageHeader, primaryBtnCls, useToast } from '../../ui';

interface NotificationPrefs {
  daily_digest: boolean;
  signature_requested: boolean;
  signature_completed: boolean;
  signature_declined: boolean;
  document_expiring: boolean;
  document_approved: boolean;
  key_document_changes: boolean;
}

const PREF_ROWS: Array<{ key: keyof NotificationPrefs; label: string; description: string }> = [
  {
    key: 'daily_digest',
    label: 'Daily digest',
    description: 'One morning email: your pending signatures, document updates, and expirations.',
  },
  {
    key: 'key_document_changes',
    label: 'Key document changes',
    description: 'Digest section when a pinned Protocol/ICF gets a new version or is re-pinned on a study you are on.',
  },
  {
    key: 'signature_requested',
    label: 'Signature requested',
    description: 'When someone routes a document for your signature (and if that request is later cancelled by a re-version).',
  },
  {
    key: 'signature_completed',
    label: 'Signature completed',
    description: 'When a document you routed is fully signed.',
  },
  {
    key: 'signature_declined',
    label: 'Signature declined',
    description: 'When a signer declines a request you sent, with their reason.',
  },
  {
    key: 'document_expiring',
    label: 'Document expiring',
    description: 'Warnings as tracked documents (certifications, licenses) approach their expiration date.',
  },
  {
    key: 'document_approved',
    label: 'Document approved',
    description: 'Reserved for the upcoming approval workflow — no emails are sent yet.',
  },
];

export function Settings() {
  const { toast } = useToast();
  const [prefs, setPrefs] = useState<NotificationPrefs | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const loadPrefs = () => {
    setLoading(true);
    setLoadError(false);
    api.get('/api/ereg/notification-preferences')
      .then((res) => setPrefs(res.data))
      .catch((err) => {
        console.error('[eReg] Failed to load notification preferences:', err);
        setLoadError(true);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadPrefs();
  }, []);

  const toggle = (key: keyof NotificationPrefs) => {
    setPrefs((prev) => (prev ? { ...prev, [key]: !prev[key] } : prev));
    setDirty(true);
  };

  const handleSave = async () => {
    if (!prefs) return;
    setSaving(true);
    try {
      const res = await api.post('/api/ereg/notification-preferences', prefs);
      setPrefs(res.data);
      setDirty(false);
      toast('Notification preferences saved');
    } catch (err) {
      console.error('[eReg] Failed to save notification preferences:', err);
      toast('Failed to save preferences', { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <PageHeader
        title="Settings"
        icon={SettingsIcon}
        subtitle="Your eReg email notification preferences"
      />

      <div className="rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
        <div className="flex items-center gap-2 border-b border-gray-100 px-4 py-3 dark:border-gray-700">
          <Bell size={16} className="text-primary-600 dark:text-primary-400" />
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Email notifications</h2>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-5 w-5 animate-spin text-primary-600 dark:text-primary-400" />
          </div>
        ) : loadError || !prefs ? (
          <div className="p-6 text-center">
            <p className="text-sm text-gray-500 dark:text-gray-400">Couldn't load your preferences.</p>
            <button onClick={loadPrefs} className="mt-2 text-sm font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300">
              Retry
            </button>
          </div>
        ) : (
          <>
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {PREF_ROWS.map((row) => (
                <label
                  key={row.key}
                  className="flex cursor-pointer items-start gap-3 px-4 py-3 transition-colors duration-150 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                >
                  <input
                    type="checkbox"
                    checked={prefs[row.key]}
                    onChange={() => toggle(row.key)}
                    className="mt-0.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600"
                  />
                  <span className="min-w-0">
                    <span className="block text-sm font-medium text-gray-900 dark:text-gray-100">{row.label}</span>
                    <span className="block text-xs text-gray-500 dark:text-gray-400">{row.description}</span>
                  </span>
                </label>
              ))}
            </div>
            <div className="flex items-center justify-end gap-3 border-t border-gray-100 px-4 py-3 dark:border-gray-700">
              <button
                onClick={handleSave}
                disabled={saving || !dirty}
                className={primaryBtnCls}
              >
                {saving ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} />}
                {saving ? 'Saving…' : 'Save Preferences'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default Settings;
